import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useUserStore } from '@/stores/userStore'
import { useAuthStore } from '@/stores/authStore'
import { useWorkoutStore, type ActiveExercise, type ActiveSet } from '@/stores/workoutStore'
import { getWorkoutDisplayName, formatWeight, parseWeightToKg, getRIRColor } from '@/lib/utils'
import { REST_TIMES } from '@/data/xframe-templates'
import { getSmartAlternatives, searchExercises } from '@/data/exercise-library'
import { ResumingWorkoutLoader } from '@/components/workout/ResumingWorkoutLoader'
import type { WorkoutType, Phase, Week, VolumeTier } from '@/types/common'

// ============================================
// SUPERSET HELPER
// ============================================

interface SupersetGroup {
  letter: string
  exerciseIndices: number[]
}

/**
 * Detect supersets from exercise slot IDs
 * Exercises like D1/D2, E1/E2 with same letter prefix are supersets
 */
function detectSupersets(exercises: ActiveExercise[]): SupersetGroup[] {
  const letterGroups: Record<string, number[]> = {}

  exercises.forEach((ex, index) => {
    // Match pattern like "D1", "D2", "E1", "E2" etc.
    const match = ex.slotId.match(/^([A-Z])(\d+)$/)
    if (match) {
      const letter = match[1] // First capture group is the letter
      if (!letterGroups[letter]) {
        letterGroups[letter] = []
      }
      letterGroups[letter].push(index)
    }
  })

  // Filter to only include letters that have more than one exercise
  return Object.entries(letterGroups)
    .filter(([_, indices]) => indices.length > 1)
    .map(([letter, exerciseIndices]) => ({ letter, exerciseIndices }))
}

// ============================================
// REST TIMER COMPONENT
// ============================================

function RestTimer() {
  const { restTimerEnd, clearRestTimer } = useWorkoutStore()
  const [timeLeft, setTimeLeft] = useState(0)

  useEffect(() => {
    if (!restTimerEnd) {
      setTimeLeft(0)
      return
    }

    const interval = setInterval(() => {
      const remaining = Math.max(0, restTimerEnd - Date.now())
      setTimeLeft(Math.ceil(remaining / 1000))

      if (remaining <= 0) {
        // Play notification sound or vibrate
        if ('vibrate' in navigator) {
          navigator.vibrate([200, 100, 200])
        }
        clearRestTimer()
      }
    }, 100)

    return () => clearInterval(interval)
  }, [restTimerEnd, clearRestTimer])

  if (!restTimerEnd || timeLeft <= 0) return null

  const minutes = Math.floor(timeLeft / 60)
  const seconds = timeLeft % 60

  return (
    <div className="fixed bottom-24 left-4 right-4 bg-primary text-white rounded-xl p-4 shadow-lg z-50 mb-safe">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm opacity-80">Rest Timer</p>
          <p className="text-3xl font-mono font-bold">
            {minutes}:{seconds.toString().padStart(2, '0')}
          </p>
        </div>
        <button
          onClick={clearRestTimer}
          className="px-4 py-2 bg-white/20 rounded-lg text-sm font-medium"
        >
          Skip
        </button>
      </div>
    </div>
  )
}

// ============================================
// WORKOUT TIMER COMPONENT
// ============================================

function WorkoutTimer({ startTime }: { startTime: number | null }) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!startTime) return

    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000))
    }, 1000)

    return () => clearInterval(interval)
  }, [startTime])

  const minutes = Math.floor(elapsed / 60)
  const seconds = elapsed % 60

  return (
    <div className="text-right">
      <p className="text-2xl font-mono text-primary">
        {minutes}:{seconds.toString().padStart(2, '0')}
      </p>
      <p className="text-xs text-text-muted">Duration</p>
    </div>
  )
}

// ============================================
// SET ROW COMPONENT
// ============================================

interface SetRowProps {
  set: ActiveSet
  exerciseIndex: number
  setIndex: number
  unitSystem: 'imperial' | 'metric'
  isCompound: boolean
  onUpdate: (updates: Partial<ActiveSet>) => void
  onComplete: () => void
  onStartRest: () => void
}

function SetRow({
  set,
  exerciseIndex: _exerciseIndex,
  setIndex: _setIndex,
  unitSystem,
  isCompound: _isCompound,
  onUpdate,
  onComplete,
  onStartRest,
}: SetRowProps) {
  const [weightInput, setWeightInput] = useState(() => {
    if (set.weightKg === null || set.weightKg === undefined) return ''
    if (set.weightKg === 0) return 'BW'
    return unitSystem === 'imperial'
      ? (set.weightKg * 2.20462).toFixed(0)
      : set.weightKg.toFixed(1)
  })
  const [repsInput, setRepsInput] = useState(set.reps?.toString() || '')

  const handleWeightChange = (value: string) => {
    setWeightInput(value)
    // Allow "BW" or "bw" for bodyweight exercises
    if (value.toLowerCase() === 'bw') {
      onUpdate({ weightKg: 0 })
      return
    }
    const parsed = parseFloat(value)
    if (!isNaN(parsed) && parsed >= 0) {
      const weightKg = parseWeightToKg(parsed, unitSystem)
      onUpdate({ weightKg })
    }
  }

  const handleRepsChange = (value: string) => {
    setRepsInput(value)
    const parsed = parseInt(value)
    if (!isNaN(parsed) && parsed > 0) {
      onUpdate({ reps: parsed })
    }
  }

  const handleRirChange = (rir: number) => {
    onUpdate({ rir })
  }

  const handleComplete = () => {
    // Allow 0 weight for bodyweight exercises
    if (set.weightKg !== null && set.weightKg !== undefined && set.reps) {
      onComplete()
      onStartRest()
    }
  }

  const setTypeLabel = {
    working: '',
    warmup: 'W',
    rest_pause: 'RP',
    drop: 'D',
  }

  return (
    <div
      className={`flex items-center gap-2 py-2 px-3 rounded-lg ${
        set.completed ? 'bg-success/10' : 'bg-elevated'
      }`}
    >
      {/* Set number */}
      <div className="w-8 text-center">
        <span className="text-sm font-medium text-text-secondary">
          {set.setNumber}
          {setTypeLabel[set.setType] && (
            <span className="text-xs text-text-muted ml-0.5">{setTypeLabel[set.setType]}</span>
          )}
        </span>
      </div>

      {/* Weight input - allows "BW" for bodyweight or numbers */}
      <div className="w-16 min-w-[4rem]">
        <input
          type="text"
          inputMode="decimal"
          value={weightInput}
          onChange={(e) => handleWeightChange(e.target.value)}
          placeholder="BW"
          className="input w-full text-center py-1.5 text-sm"
          disabled={set.completed}
        />
      </div>

      {/* Reps input */}
      <div className="w-16">
        <input
          type="number"
          inputMode="numeric"
          value={repsInput}
          onChange={(e) => handleRepsChange(e.target.value)}
          placeholder="reps"
          className="input w-full text-center py-1.5 text-sm"
          disabled={set.completed}
        />
      </div>

      {/* RIR selector */}
      <div className="w-20">
        <select
          value={set.rir ?? ''}
          onChange={(e) => handleRirChange(parseInt(e.target.value))}
          className={`input w-full text-center py-1.5 text-sm ${getRIRColor(set.rir ?? 3)}`}
          disabled={set.completed}
        >
          <option value="">RIR</option>
          {[0, 1, 2, 3, 4, 5].map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      {/* Complete button */}
      <button
        onClick={handleComplete}
        disabled={set.completed || !set.weightKg || !set.reps}
        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
          set.completed
            ? 'bg-success text-white'
            : set.weightKg && set.reps
            ? 'bg-primary text-white'
            : 'bg-elevated text-text-muted'
        }`}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  )
}

// ============================================
// SMART SUBSTITUTION MODAL
// ============================================

// Muscle groups for custom exercise creation
const MUSCLE_GROUPS = [
  { value: 'chest', label: 'Chest' },
  { value: 'back_lats', label: 'Back (Lats)' },
  { value: 'back_upper', label: 'Back (Upper/Traps)' },
  { value: 'shoulders_front', label: 'Shoulders (Front Delts)' },
  { value: 'shoulders_side', label: 'Shoulders (Side Delts)' },
  { value: 'shoulders_rear', label: 'Shoulders (Rear Delts)' },
  { value: 'biceps', label: 'Biceps' },
  { value: 'triceps', label: 'Triceps' },
  { value: 'quads', label: 'Quads' },
  { value: 'hamstrings', label: 'Hamstrings' },
  { value: 'glutes', label: 'Glutes' },
  { value: 'calves', label: 'Calves' },
  { value: 'core', label: 'Core' },
]

interface SmartSubstitutionModalProps {
  exerciseName: string
  fallbackAlternatives: string[]
  onSelect: (name: string) => void
  onClose: () => void
}

function SmartSubstitutionModal({
  exerciseName,
  fallbackAlternatives,
  onSelect,
  onClose,
}: SmartSubstitutionModalProps) {
  const { user } = useAuthStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<'recommended' | 'search'>('recommended')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Custom exercise form state
  const [customName, setCustomName] = useState('')
  const [customMuscleGroup, setCustomMuscleGroup] = useState('')
  const [customIsCompound, setCustomIsCompound] = useState(false)

  // Get smart alternatives from exercise library
  const smartAlternatives = useMemo(
    () => getSmartAlternatives(exerciseName, 8),
    [exerciseName]
  )

  // Search results (including custom exercises)
  const searchResults = useMemo(() => {
    if (searchQuery.length < 2) return []
    return searchExercises(searchQuery).slice(0, 15)
  }, [searchQuery])

  // Check if we have library matches
  const hasLibraryMatches = smartAlternatives.sameBias.length > 0 || smartAlternatives.sameMuscle.length > 0

  // Handle custom exercise creation
  const handleCreateCustomExercise = async () => {
    if (!customName.trim()) {
      setCreateError('Exercise name is required')
      return
    }
    if (!customMuscleGroup) {
      setCreateError('Please select a target muscle')
      return
    }
    if (!user?.id) {
      setCreateError('You must be logged in')
      return
    }

    setIsCreating(true)
    setCreateError(null)

    try {
      // Import dynamically to avoid circular dependencies
      const { createCustomExercise, customExerciseExists } = await import('@/lib/db')

      // Check if exercise already exists
      const exists = await customExerciseExists(user.id, customName.trim())
      if (exists) {
        setCreateError('An exercise with this name already exists')
        setIsCreating(false)
        return
      }

      // Create the custom exercise
      await createCustomExercise({
        user_id: user.id,
        name: customName.trim(),
        muscle_group: customMuscleGroup,
        is_compound: customIsCompound,
      })

      // Select the new exercise and close
      onSelect(customName.trim())
    } catch (error) {
      console.error('Create custom exercise error:', error)
      setCreateError('Failed to create exercise. Please try again.')
      setIsCreating(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-text-primary">Substitute Exercise</h2>
            <p className="text-xs text-text-muted">Replace: {exerciseName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-elevated rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('recommended')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              activeTab === 'recommended'
                ? 'text-primary border-b-2 border-primary'
                : 'text-text-muted'
            }`}
          >
            Recommended
          </button>
          <button
            onClick={() => setActiveTab('search')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              activeTab === 'search'
                ? 'text-primary border-b-2 border-primary'
                : 'text-text-muted'
            }`}
          >
            Search
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'recommended' ? (
            <div className="space-y-4">
              {/* Same Movement Bias (Best matches) */}
              {smartAlternatives.sameBias.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                    Same Movement Pattern
                  </h3>
                  <div className="space-y-1">
                    {smartAlternatives.sameBias.map((ex) => (
                      <button
                        key={ex.id}
                        onClick={() => onSelect(ex.name)}
                        className="w-full p-3 bg-elevated hover:bg-primary/10 rounded-lg text-left transition-colors"
                      >
                        <p className="font-medium text-text-primary">{ex.name}</p>
                        <p className="text-xs text-text-muted">
                          {ex.equipment.join(' / ')}
                          {ex.isUnilateral && ' • Unilateral'}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Same Muscle Group */}
              {smartAlternatives.sameMuscle.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                    Same Muscle Group
                  </h3>
                  <div className="space-y-1">
                    {smartAlternatives.sameMuscle.slice(0, 5).map((ex) => (
                      <button
                        key={ex.id}
                        onClick={() => onSelect(ex.name)}
                        className="w-full p-3 bg-elevated hover:bg-primary/10 rounded-lg text-left transition-colors"
                      >
                        <p className="font-medium text-text-primary">{ex.name}</p>
                        <p className="text-xs text-text-muted">
                          {ex.equipment.join(' / ')}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Fallback to hardcoded alternatives if no library matches */}
              {!hasLibraryMatches && fallbackAlternatives.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                    Program Alternatives
                  </h3>
                  <div className="space-y-1">
                    {fallbackAlternatives.map((alt) => (
                      <button
                        key={alt}
                        onClick={() => onSelect(alt)}
                        className="w-full p-3 bg-elevated hover:bg-primary/10 rounded-lg text-left transition-colors"
                      >
                        <p className="font-medium text-text-primary">{alt}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!hasLibraryMatches && fallbackAlternatives.length === 0 && (
                <p className="text-center text-text-muted py-8">
                  No alternatives found. Try searching.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Search Input */}
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search exercises..."
                className="w-full p-3 bg-elevated border border-border rounded-lg text-text-primary placeholder-text-muted"
                autoFocus
              />

              {/* Search Results */}
              {searchResults.length > 0 ? (
                <div className="space-y-1">
                  {searchResults.map((ex) => (
                    <button
                      key={ex.id}
                      onClick={() => onSelect(ex.name)}
                      className="w-full p-3 bg-elevated hover:bg-primary/10 rounded-lg text-left transition-colors"
                    >
                      <p className="font-medium text-text-primary">{ex.name}</p>
                      <p className="text-xs text-text-muted">
                        {ex.muscleGroup.replace('_', ' ')} • {ex.equipment.join(' / ')}
                      </p>
                    </button>
                  ))}
                </div>
              ) : searchQuery.length >= 2 ? (
                <p className="text-center text-text-muted py-8">
                  No exercises found for "{searchQuery}"
                </p>
              ) : (
                <p className="text-center text-text-muted py-8">
                  Type at least 2 characters to search
                </p>
              )}
            </div>
          )}
        </div>

        {/* Create Custom Exercise Section */}
        <div className="border-t border-border p-4">
          {!showCreateForm ? (
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full py-3 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Custom Exercise
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-text-primary">Create Custom Exercise</h3>
                <button
                  onClick={() => {
                    setShowCreateForm(false)
                    setCreateError(null)
                  }}
                  className="text-xs text-text-muted hover:text-text-primary"
                >
                  Cancel
                </button>
              </div>

              {/* Exercise Name */}
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Exercise name"
                className="w-full p-3 bg-elevated border border-border rounded-lg text-text-primary placeholder-text-muted text-sm"
                autoFocus
              />

              {/* Target Muscle */}
              <select
                value={customMuscleGroup}
                onChange={(e) => setCustomMuscleGroup(e.target.value)}
                className="w-full p-3 bg-elevated border border-border rounded-lg text-text-primary text-sm"
              >
                <option value="">Select target muscle...</option>
                {MUSCLE_GROUPS.map((mg) => (
                  <option key={mg.value} value={mg.value}>
                    {mg.label}
                  </option>
                ))}
              </select>

              {/* Movement Type Toggle */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="movementType"
                    checked={!customIsCompound}
                    onChange={() => setCustomIsCompound(false)}
                    className="text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-text-secondary">Isolation</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="movementType"
                    checked={customIsCompound}
                    onChange={() => setCustomIsCompound(true)}
                    className="text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-text-secondary">Compound</span>
                </label>
              </div>

              {/* Error Message */}
              {createError && (
                <p className="text-xs text-error">{createError}</p>
              )}

              {/* Create Button */}
              <button
                onClick={handleCreateCustomExercise}
                disabled={isCreating || !customName.trim() || !customMuscleGroup}
                className="w-full py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create & Use'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================
// EXERCISE CARD COMPONENT
// ============================================

interface ExerciseCardProps {
  exercise: ActiveExercise
  exerciseIndex: number
  isActive: boolean
  unitSystem: 'imperial' | 'metric'
  onSelect: () => void
  supersetInfo?: {
    letter: string
    position: 'first' | 'middle' | 'last' | 'only'
    partnerNames: string[]
  }
}

function ExerciseCard({
  exercise,
  exerciseIndex,
  isActive,
  unitSystem,
  onSelect,
  supersetInfo,
}: ExerciseCardProps) {
  const { updateSet, completeSet, addSet, substituteExercise, startRestTimer } = useWorkoutStore()
  const [showSubstitutionModal, setShowSubstitutionModal] = useState(false)

  const completedSets = exercise.sets.filter((s) => s.completed).length
  const totalSets = exercise.sets.length
  const isComplete = completedSets === totalSets && totalSets > 0

  const restTime = exercise.isCompound ? REST_TIMES.compound.default : REST_TIMES.isolation.default

  const handleSubstitute = (newName: string) => {
    substituteExercise(exerciseIndex, newName)
    setShowSubstitutionModal(false)
  }

  // Superset indicator colors
  const supersetColors: Record<string, string> = {
    D: 'bg-purple-500',
    E: 'bg-orange-500',
    F: 'bg-teal-500',
    G: 'bg-pink-500',
  }

  return (
    <div className="relative">
      {/* Superset connector line */}
      {supersetInfo && supersetInfo.position !== 'only' && (
        <div
          className={`absolute left-4 w-1 ${supersetColors[supersetInfo.letter] || 'bg-accent'} ${
            supersetInfo.position === 'first'
              ? 'top-1/2 bottom-0 rounded-t-full'
              : supersetInfo.position === 'last'
              ? 'top-0 bottom-1/2 rounded-b-full'
              : 'top-0 bottom-0'
          }`}
          style={{ zIndex: 10 }}
        />
      )}

      <div
        className={`card transition-all ${supersetInfo ? 'ml-6' : ''} ${
          isActive ? 'ring-2 ring-primary' : isComplete ? 'opacity-60' : ''
        }`}
        onClick={() => !isActive && onSelect()}
      >
        {/* Superset badge */}
        {supersetInfo && (
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs font-bold text-white px-2 py-0.5 rounded ${supersetColors[supersetInfo.letter] || 'bg-accent'}`}>
              SUPERSET
            </span>
            <span className="text-xs text-text-muted">
              with {supersetInfo.partnerNames.join(', ')}
            </span>
          </div>
        )}

        {/* Exercise Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                supersetInfo
                  ? `${supersetColors[supersetInfo.letter] || 'bg-accent'} text-white`
                  : 'text-primary bg-primary/10'
              }`}>
                {exercise.slotId}
              </span>
              {isComplete && (
                <span className="text-xs text-success">Complete</span>
              )}
            </div>
            <h3 className="font-semibold text-text-primary mt-1">{exercise.name}</h3>
            <p className="text-sm text-text-muted">
              {exercise.targetSets} × {exercise.reps}
              {exercise.notes && !supersetInfo && <span className="ml-2 text-text-secondary">({exercise.notes})</span>}
            </p>
            {/* Substitute button - only show when active */}
            {isActive && (
              <button
                onClick={() => setShowSubstitutionModal(true)}
                className="mt-2 text-xs text-primary hover:underline"
              >
                Substitute exercise...
              </button>
            )}
          </div>

        <div className="text-right">
          <p className="text-lg font-semibold text-text-primary">
            {completedSets}/{totalSets}
          </p>
          <p className="text-xs text-text-muted">sets</p>
        </div>
      </div>

      {/* Sets (only show if active) */}
      {isActive && (
        <div className="space-y-2">
          {/* Header */}
          <div className="flex items-center gap-2 px-3 text-xs text-text-muted">
            <div className="w-8 text-center">Set</div>
            <div className="flex-1 text-center">{unitSystem === 'imperial' ? 'lbs' : 'kg'}</div>
            <div className="w-16 text-center">Reps</div>
            <div className="w-20 text-center">RIR</div>
            <div className="w-10"></div>
          </div>

          {/* Set rows */}
          {exercise.sets.map((set, setIndex) => (
            <SetRow
              key={set.id}
              set={set}
              exerciseIndex={exerciseIndex}
              setIndex={setIndex}
              unitSystem={unitSystem}
              isCompound={exercise.isCompound}
              onUpdate={(updates) => updateSet(exerciseIndex, setIndex, updates)}
              onComplete={() => completeSet(exerciseIndex, setIndex)}
              onStartRest={() => startRestTimer(restTime)}
            />
          ))}

          {/* Add set buttons */}
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => addSet(exerciseIndex, 'working')}
              className="flex-1 py-2 text-sm text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
            >
              + Add Set
            </button>
            <button
              onClick={() => addSet(exerciseIndex, 'rest_pause')}
              className="px-3 py-2 text-sm text-text-secondary border border-elevated rounded-lg hover:bg-elevated transition-colors"
            >
              + RP
            </button>
            <button
              onClick={() => addSet(exerciseIndex, 'drop')}
              className="px-3 py-2 text-sm text-text-secondary border border-elevated rounded-lg hover:bg-elevated transition-colors"
            >
              + Drop
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Smart Substitution Modal */}
      {showSubstitutionModal && (
        <SmartSubstitutionModal
          exerciseName={exercise.name}
          fallbackAlternatives={exercise.alternatives}
          onSelect={handleSubstitute}
          onClose={() => setShowSubstitutionModal(false)}
        />
      )}
    </div>
  )
}

// ============================================
// WORKOUT SUMMARY MODAL
// ============================================

interface WorkoutSummaryProps {
  onConfirm: () => void
  onCancel: () => void
}

function WorkoutSummary({ onConfirm, onCancel }: WorkoutSummaryProps) {
  const { activeExercises, workoutStartTime } = useWorkoutStore()
  const { profile } = useUserStore()

  const totalSets = activeExercises.reduce(
    (sum, ex) => sum + ex.sets.filter((s) => s.completed).length,
    0
  )
  const totalVolume = activeExercises.reduce((sum, ex) => {
    return (
      sum +
      ex.sets
        .filter((s) => s.completed && s.weightKg && s.reps && s.setType === 'working')
        .reduce((setSum, s) => setSum + (s.weightKg || 0) * (s.reps || 0), 0)
    )
  }, 0)

  const duration = workoutStartTime ? Math.round((Date.now() - workoutStartTime) / 60000) : 0

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl p-6 max-w-sm w-full">
        <h2 className="text-xl font-bold text-text-primary mb-4">Workout Complete!</h2>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{totalSets}</p>
            <p className="text-xs text-text-muted">Sets</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">
              {formatWeight(totalVolume, profile?.unit_system || 'imperial', 0).split(' ')[0]}
            </p>
            <p className="text-xs text-text-muted">Volume</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{duration}</p>
            <p className="text-xs text-text-muted">Minutes</p>
          </div>
        </div>

        <div className="space-y-2 mb-6">
          {activeExercises.map((ex) => {
            const completed = ex.sets.filter((s) => s.completed).length
            return (
              <div key={ex.slotId} className="flex justify-between text-sm">
                <span className="text-text-secondary">{ex.name}</span>
                <span className="text-text-muted">
                  {completed}/{ex.sets.length} sets
                </span>
              </div>
            )
          })}
        </div>

        <div className="flex gap-3">
          <button onClick={onCancel} className="btn-secondary flex-1">
            Continue
          </button>
          <button onClick={onConfirm} className="btn-primary flex-1">
            Save Workout
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================
// MAIN WORKOUT SESSION PAGE
// ============================================

export default function WorkoutSession() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const workoutType = searchParams.get('type') as WorkoutType | null
  const isResumedParam = searchParams.get('resumed') === 'true'

  const { user } = useAuthStore()
  const { profile } = useUserStore()
  const {
    activeSession,
    activeExercises,
    currentExerciseIndex,
    workoutStartTime,
    isResuming,
    startWorkout,
    cancelWorkout,
    finishWorkout,
    setCurrentExercise,
    saveWorkoutState,
  } = useWorkoutStore()

  const [showSummary, setShowSummary] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [showResumeLoader, setShowResumeLoader] = useState(isResumedParam)

  // Hide resume loader after brief display
  useEffect(() => {
    if (showResumeLoader && activeSession) {
      const timer = setTimeout(() => setShowResumeLoader(false), 1500)
      return () => clearTimeout(timer)
    }
  }, [showResumeLoader, activeSession])

  // Start workout if we have a type but no active session (and not resuming)
  useEffect(() => {
    if (workoutType && !activeSession && !isResumedParam && user?.id && profile) {
      startWorkout(
        user.id,
        workoutType,
        profile.current_phase as Phase,
        profile.current_week as Week,
        profile.volume_tier as VolumeTier
      )
    }
  }, [workoutType, activeSession, isResumedParam, user?.id, profile, startWorkout])

  // Auto-save workout state on visibility change (when user tabs away or locks phone)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && activeSession) {
        saveWorkoutState()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [activeSession, saveWorkoutState])

  // Save state before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (activeSession) {
        saveWorkoutState()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [activeSession, saveWorkoutState])

  const handleCancel = useCallback(() => {
    cancelWorkout()
    navigate('/logbook')
  }, [cancelWorkout, navigate])

  const handleFinish = useCallback(async () => {
    await finishWorkout()
    navigate('/logbook')
  }, [finishWorkout, navigate])

  // Compute superset groups for visual indicators
  // NOTE: These hooks MUST be before any early returns to satisfy Rules of Hooks
  const supersetGroups = useMemo(() => detectSupersets(activeExercises), [activeExercises])

  // Build a map of exercise index -> superset info
  const supersetInfoMap = useMemo(() => {
    const map: Record<number, ExerciseCardProps['supersetInfo']> = {}

    for (const group of supersetGroups) {
      const indices = group.exerciseIndices.sort((a, b) => a - b)

      for (let i = 0; i < indices.length; i++) {
        const exIndex = indices[i]
        const partnerNames = indices
          .filter((idx) => idx !== exIndex)
          .map((idx) => activeExercises[idx]?.name || '')

        map[exIndex] = {
          letter: group.letter,
          position: indices.length === 1
            ? 'only'
            : i === 0
              ? 'first'
              : i === indices.length - 1
                ? 'last'
                : 'middle',
          partnerNames,
        }
      }
    }

    return map
  }, [supersetGroups, activeExercises])

  // No workout type provided AND not resuming - redirect to logbook
  if (!workoutType && !isResumedParam && !activeSession) {
    return (
      <div className="p-4">
        <div className="card text-center py-8">
          <h2 className="text-lg font-semibold text-text-primary mb-2">No Workout Selected</h2>
          <p className="text-text-muted mb-4">Please select a workout type from the Logbook.</p>
          <button onClick={() => navigate('/logbook')} className="btn-primary">
            Go to Logbook
          </button>
        </div>
      </div>
    )
  }

  // Loading state (or resuming)
  if (!activeSession || activeExercises.length === 0) {
    return (
      <div className="p-4">
        <ResumingWorkoutLoader isVisible={isResuming || showResumeLoader} />
        <div className="card text-center py-8">
          <div className="animate-pulse">
            <div className="w-12 h-12 bg-primary/20 rounded-full mx-auto mb-4" />
            <p className="text-text-muted">
              {isResuming ? 'Resuming workout...' : 'Loading workout...'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Get display name from session (works for both new and resumed workouts)
  const displayWorkoutType = activeSession.workout_type as WorkoutType

  const completedExercises = activeExercises.filter(
    (ex) => ex.sets.filter((s) => s.completed).length === ex.sets.length
  ).length

  return (
    <div className="pb-24">
      {/* Resume loader overlay */}
      <ResumingWorkoutLoader isVisible={showResumeLoader} />

      {/* Workout Progress Header - sticky below main header */}
      <div className="sticky top-0 bg-background z-30 p-4 border-b border-elevated">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-text-primary">
              {getWorkoutDisplayName(displayWorkoutType)}
            </h1>
            <p className="text-sm text-text-muted">
              Phase {activeSession.phase} · Week {activeSession.week}
              {activeSession.is_deload && ' · Deload'}
            </p>
          </div>
          <WorkoutTimer startTime={workoutStartTime} />
        </div>

        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex justify-between text-xs text-text-muted mb-1">
            <span>Progress</span>
            <span>
              {completedExercises}/{activeExercises.length} exercises
            </span>
          </div>
          <div className="h-2 bg-elevated rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(completedExercises / activeExercises.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Exercise List */}
      <div className="p-4 space-y-3">
        {activeExercises.map((exercise, index) => (
          <ExerciseCard
            key={exercise.exerciseLogId}
            exercise={exercise}
            exerciseIndex={index}
            isActive={index === currentExerciseIndex}
            unitSystem={profile?.unit_system || 'imperial'}
            onSelect={() => setCurrentExercise(index)}
            supersetInfo={supersetInfoMap[index]}
          />
        ))}

        {/* Workout Actions - at end of exercise list */}
        <div className="pt-4 pb-8 border-t border-border mt-6">
          <div className="flex gap-3">
            <button
              onClick={() => setShowCancelConfirm(true)}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button onClick={() => setShowSummary(true)} className="btn-primary flex-1">
              Finish Workout
            </button>
          </div>
        </div>
      </div>

      {/* Rest Timer */}
      <RestTimer />

      {/* Summary Modal */}
      {showSummary && (
        <WorkoutSummary onConfirm={handleFinish} onCancel={() => setShowSummary(false)} />
      )}

      {/* Cancel Confirmation */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl p-6 max-w-sm w-full">
            <h2 className="text-lg font-bold text-text-primary mb-2">Cancel Workout?</h2>
            <p className="text-text-secondary mb-4">
              Your progress will not be saved. Are you sure?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="btn-secondary flex-1"
              >
                Keep Going
              </button>
              <button onClick={handleCancel} className="btn-primary flex-1 bg-error">
                Cancel Workout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
